import { createSelector } from '@reduxjs/toolkit'
import { createSelectorCache, getOrBuildSelector } from '../../Sinks/Selector/SelectorCache.ts'
import type { KronaTraitFilterOpts, TransformResult } from '../Types.ts'
import type { State } from '../../../../../Flux/Slice/index.ts'
import type { SourceId } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { NO_TRAIT } from '../../../../../Flux/Slice/Visualization/VisualizationSliceTypes.ts'
import { selectFilteredCube } from '../../Sinks/Selector/SelectFilteredCube.ts'
import { forwardCubeStatus, selectCubeContext } from '../../Sinks/Selector/CubeContext.ts'
import { buildTraitsByTaxonMap, normTaxon } from './TraitCommon.ts'
import { axisLabelOf } from '../../Sinks/Selector/axisLabel.js'

export type TraitStackedBarSeries = {
  data: Array<Record<string, number | string>>
  stackKeys: string[]
  /** Samples per bucket, aligned to `data` by index. Beside the rows — see TaxaStackedBarCore. */
  sampleCounts: (number | undefined)[]
}

const selectorCache = createSelectorCache<TransformResult<TraitStackedBarSeries>>()

export function selectTraitStackedBarSeries(
  state: typeof State,
  sourceId: SourceId,
  _bucketKey: string | undefined,
  opts: KronaTraitFilterOpts,
  optsKey: string
): TransformResult<TraitStackedBarSeries> {
  const cacheId = `${sourceId}|${optsKey}`

  return getOrBuildSelector(selectorCache, cacheId, () =>
    createSelector(
      [
        (s: typeof State) => selectCubeContext(s, sourceId),
        (s: typeof State) =>
          selectFilteredCube(s, sourceId, { selectedPath: opts.selectedPath, gradeThreshold: opts.gradeThreshold }),
        (s: typeof State) => s.visualization.traits?.traits ?? [],
        (s: typeof State) => s.visualization.sources[sourceId]?.shared?.natureKPIs
      ],
      (ctx, filteredBuckets, allTraits, natureKPIs): TransformResult<TraitStackedBarSeries> => {
        if (ctx.status !== 'ready') return forwardCubeStatus(ctx)

        const scalingMode = ctx.scalingMode

        // (normalized taxon name) -> [traitname, ...] for the selected group
        const traitsByTaxon = buildTraitsByTaxonMap(allTraits, opts.traitGroup)

        // Aggregate trait counts per bucket from original bucket.values (preserves lineage)
        const traitSet = new Set<string>()

        const data = filteredBuckets.map((bucket) => {
          const out: Record<string, number | string> = { xAxisvalue: axisLabelOf(bucket.bucketLabel) }
          const local: Record<string, number> = {}

          const values = bucket.values ?? []
          const isPct = scalingMode === 'PERCENTAGE'
          const excludeNoTrait = natureKPIs?.noTraitMode === 'exclude'

          for (const v of values) {
            const mag = v.magnitude ?? 0
            if (!mag) continue

            const lineage = v.taxa ?? []
            const traitNamesForRow = new Set<string>()

            for (let i = 0; i < lineage.length; i++) {
              const taxName = normTaxon(lineage[i] ?? '')
              if (!taxName) continue
              const list = traitsByTaxon.get(taxName) ?? []

              for (let j = 0; j < list.length; j++) {
                const tr = list[j]!
                traitNamesForRow.add(tr)
              }
            }

            if (traitNamesForRow.size === 0) {
              // preserve mass unless user explicitly excludes untraited
              if (!excludeNoTrait) {
                local[NO_TRAIT] = (local[NO_TRAIT] ?? 0) + mag
              }
            } else if (isPct) {
              const traits = Array.from(traitNamesForRow)
              const share = mag / traits.length
              for (let i = 0; i < traits.length; i++) {
                const tr = traits[i]!
                local[tr] = (local[tr] ?? 0) + share
              }
            } else {
              for (const tr of traitNamesForRow) {
                local[tr] = (local[tr] ?? 0) + mag
              }
            }
          }

          if (isPct) {
            if (excludeNoTrait && local[NO_TRAIT] !== undefined) {
              /* ensure this is actually not present if user requested exclusion */
              delete local[NO_TRAIT]
            }
            const total = Object.values(local).reduce((a, b) => a + (Number(b) || 0), 0)
            if (total > 0) {
              const factor = 100 / total
              for (const k of Object.keys(local)) {
                local[k] = (local[k] ?? 0) * factor
              }
            }
          }

          for (const k of Object.keys(local)) {
            traitSet.add(k)
            out[k] = local[k]!
          }

          return out
        })

        const stackKeys = Array.from(traitSet).sort((a, b) => a.localeCompare(b))

        // Ensure all rows have all keys (fill with 0)
        for (let i = 0; i < data.length; i++) {
          const row = data[i]!
          for (let j = 0; j < stackKeys.length; j++) {
            const k = stackKeys[j]!
            if (row[k] === undefined) row[k] = 0
          }
        }

        return { status: 'ready', value: { data, stackKeys, sampleCounts: filteredBuckets.map((b) => b.sampleCount) } }
      }
    )

  )(state)
}
